'use client';

import { useCallback } from 'react';
import {
  buildBatchLabels,
  buildGridZpl,
  printGridLabels,
  printLabels,
  type QrLabelInput,
  type PrintLabelsResult,
  type PrintGridLabelsResult
} from '@/lib/zebra';
import {
  useActiveZebraPrinterUid,
  useContinuousPrintConfig,
  useGridPrintConfig,
  useLabelPrintConfig,
  useZebraPrinterProfiles
} from './use-app-settings';

export type SingleQrPrintResult = PrintLabelsResult | PrintGridLabelsResult;

export type CopyZplResult = { ok: true } | { ok: false; error: string };

/**
 * Single-QR Zebra print that respects the global print-mode setting.
 *
 * When `print_mode === 'continuous'` and `continuous_columns > 1`, the
 * single label is duplicated `cols` times and emitted as a one-row grid
 * feed — every sticker across the roll's row carries the same QR. This
 * stops the printer from feeding the full row pitch but only inking the
 * first column, which would discard `cols − 1` blank stickers per print.
 *
 * In sticker mode (or when columns = 1) it behaves exactly like the bare
 * `printLabels` call it replaces.
 *
 * Returns null for `printerUid` so callers can keep their own
 * `if (!printerUid) window.print()` fallback before calling.
 */
export function useSingleQrPrint() {
  const {
    labelWidthMm,
    labelHeightMm,
    gapMm,
    labelDpi,
    qrHeaderGapMm,
    qrFooterGapMm,
    qrHeaderFontMm,
    qrFooterFontMm
  } = useLabelPrintConfig();
  const continuousConfig = useContinuousPrintConfig();
  const gridConfig = useGridPrintConfig();
  const printerUid = useActiveZebraPrinterUid();
  const profiles = useZebraPrinterProfiles();

  const printSingleQr = useCallback(
    async (label: QrLabelInput): Promise<SingleQrPrintResult> => {
      if (!printerUid) {
        return {
          ok: false,
          reason: 'no-printer',
          error: 'No active Zebra printer'
        };
      }

      if (gridConfig.mode === 'continuous' && continuousConfig.columns > 1) {
        const cols = continuousConfig.columns;
        const repeated = Array.from({ length: cols }, () => label);
        const paperWidthMm =
          cols * labelWidthMm +
          (cols - 1) * gapMm +
          2 * continuousConfig.edgeMarginMm;
        const paperHeightMm = labelHeightMm + gapMm;
        return printGridLabels({
          printerUid,
          labels: repeated,
          profiles,
          layout: {
            paperWidthMm,
            paperHeightMm,
            cols,
            rows: 1,
            marginMm: continuousConfig.edgeMarginMm,
            gapMm,
            dpi: labelDpi,
            qrHeaderGapMm,
            qrFooterGapMm,
            qrHeaderFontMm,
            qrFooterFontMm
          }
        });
      }

      return printLabels({
        printerUid,
        labels: [label],
        profiles,
        fallbackOptions: {
          widthMm: labelWidthMm,
          heightMm: labelHeightMm,
          dpi: labelDpi,
          qrHeaderGapMm,
          qrFooterGapMm,
          qrHeaderFontMm,
          qrFooterFontMm
        }
      });
    },
    [
      printerUid,
      profiles,
      gridConfig.mode,
      continuousConfig.columns,
      continuousConfig.edgeMarginMm,
      labelWidthMm,
      labelHeightMm,
      gapMm,
      labelDpi,
      qrHeaderGapMm,
      qrFooterGapMm,
      qrHeaderFontMm,
      qrFooterFontMm
    ]
  );

  // Pure ZPL preview/debug helper — mirrors `printSingleQr`'s continuous-vs-
  // sticker branching but builds the raw string instead of sending it to a
  // printer, so it works with no `printerUid` configured (paste into
  // labelary.com/viewer.html to preview without hardware).
  const copySingleQrZpl = useCallback(
    async (label: QrLabelInput): Promise<CopyZplResult> => {
      let zpl: string;
      if (gridConfig.mode === 'continuous' && continuousConfig.columns > 1) {
        const cols = continuousConfig.columns;
        const repeated = Array.from({ length: cols }, () => label);
        const paperWidthMm =
          cols * labelWidthMm +
          (cols - 1) * gapMm +
          2 * continuousConfig.edgeMarginMm;
        const paperHeightMm = labelHeightMm + gapMm;
        zpl = buildGridZpl(repeated, {
          paperWidthMm,
          paperHeightMm,
          cols,
          rows: 1,
          marginMm: continuousConfig.edgeMarginMm,
          gapMm,
          dpi: labelDpi,
          qrHeaderGapMm,
          qrFooterGapMm,
          qrHeaderFontMm,
          qrFooterFontMm
        });
      } else {
        zpl = buildBatchLabels([label], {
          widthMm: labelWidthMm,
          heightMm: labelHeightMm,
          dpi: labelDpi,
          qrHeaderGapMm,
          qrFooterGapMm,
          qrHeaderFontMm,
          qrFooterFontMm
        });
      }

      try {
        await navigator.clipboard.writeText(zpl);
        return { ok: true };
      } catch {
        return { ok: false, error: 'Could not copy to clipboard' };
      }
    },
    [
      gridConfig.mode,
      continuousConfig.columns,
      continuousConfig.edgeMarginMm,
      labelWidthMm,
      labelHeightMm,
      gapMm,
      labelDpi,
      qrHeaderGapMm,
      qrFooterGapMm,
      qrHeaderFontMm,
      qrFooterFontMm
    ]
  );

  return { printerUid, printSingleQr, copySingleQrZpl };
}
